import React, { useEffect, useRef } from "react";

interface CircularProgressRingProps {
  value: number;
  size?: number;
  strokeWidth?: number;
  labelClassName?: string;
  onClick?: () => void;
}

const FILL_COLOR = "#ff0990";
const TRACK_COLOR = "#f1f1f1";

const CircularProgressRing: React.FC<CircularProgressRingProps> = ({
  value,
  size = 96,
  strokeWidth = 6,
  labelClassName,
  onClick,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const safeValue = Math.min(100, Math.max(0, Math.round(Number(value) || 0)));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const dpr = Math.max(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    const center = size / 2;
    const radius = (size - strokeWidth) / 2 - 0.5;
    const startAngle = -Math.PI / 2;

    ctx.lineWidth = strokeWidth;
    ctx.lineCap = "round";

    ctx.beginPath();
    ctx.strokeStyle = TRACK_COLOR;
    ctx.arc(center, center, radius, 0, Math.PI * 2);
    ctx.stroke();

    if (safeValue <= 0) {
      return;
    }

    ctx.beginPath();
    ctx.strokeStyle = FILL_COLOR;
    if (safeValue >= 100) {
      ctx.lineCap = "butt";
      ctx.arc(center, center, radius, 0, Math.PI * 2);
    } else {
      const endAngle = startAngle + (safeValue / 100) * Math.PI * 2;
      ctx.arc(center, center, radius, startAngle, endAngle);
    }
    ctx.stroke();
  }, [safeValue, size, strokeWidth]);

  return (
    <div
      className="profile-progress-ring"
      onClick={onClick}
      style={{ width: size, height: size }}
      role="img"
      aria-label={`${safeValue} percent complete`}
    >
      <canvas ref={canvasRef} />
      <span className={`profile-progress-ring__label ${labelClassName || ""}`}>
        {safeValue}%
      </span>
    </div>
  );
};

export default CircularProgressRing;
